import React, {useEffect} from "react";
import {FullBotUi} from "../../../../../api/generated";
import {useAppDispatch, useAppSelector} from "../../../../../app/hooks";
import {RequestStatus} from "../../../../../api/apiConfiguration";
import {Button, ButtonState} from "../../../../elements/button/Button";
import {CustomSwitchFieldWithTitle} from "../../../../elements/customSwitchFieldWithTitle/CustomSwitchFieldWithTitle";
import {SecondaryCollapsibleSection} from "../../../../elements/collapsableSection/secondary/SecondaryCollapsibleSection";
import {Section} from "../../../../elements/section/Section";
import {ValidatedForm, FormContext} from "../../../../elements/validator/validatedForm/ValidatedForm";
import {showErrorToastNotification, showSuccessToastNotification} from "../../../../elements/toastNotification/toastNotificationSlice";
import {useUnsavedChangesWarning} from "../../../../../hooks/useUnsavedChangesWarning";
import {
    fetchTranscriptRoutingSettings,
    hasUnsavedTranscriptRoutingChangesSelector,
    resetTranscriptRoutingSettings,
    setTranscriptRoutingEnabled,
    TranscriptRoutingSettingsUI,
    transcriptRoutingEnabledSelector,
    transcriptRoutingRequestStatusSelector,
    transcriptRoutingUpdateStatusSelector,
    updateTranscriptRoutingSettings
} from "./transcriptRoutingSlice";
import styles from "./TranscriptRoutingTab.module.css";
import {TranscriptRoutingDepartments} from "./TranscriptRoutingDepartments";
import {TranscriptRoutingLocations} from "./TranscriptRoutingLocations";

interface TranscriptRoutingTabProps {
    bot: FullBotUi;
}

interface ValidateWhileEditingProps {
    editing: boolean;
    formContext: FormContext<TranscriptRoutingSettingsUI>;
}

// Flags empty required fields as soon as the routing is edited rather than after a save attempt,
// so Save Changes stays disabled while the configuration is incomplete.
const ValidateWhileEditing: React.FC<ValidateWhileEditingProps> = ({editing, formContext}) => {
    const {formState, setFormState} = formContext;

    useEffect(() => {
        if (formState.formDirty !== editing) {
            setFormState({...formState, formDirty: editing});
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editing]);

    return null;
};

export const TranscriptRoutingTab: React.FC<TranscriptRoutingTabProps> = ({bot}) => {
    const dispatch = useAppDispatch();
    const requestStatus = useAppSelector(transcriptRoutingRequestStatusSelector);
    const updateStatus = useAppSelector(transcriptRoutingUpdateStatusSelector);
    const hasUnsavedChanges = useAppSelector(hasUnsavedTranscriptRoutingChangesSelector);

    useUnsavedChangesWarning(hasUnsavedChanges);

    useEffect(() => {
        dispatch(fetchTranscriptRoutingSettings(bot.publicId)).unwrap()
            .catch(() => dispatch(showErrorToastNotification({message: "Failed to load transcript routing settings"})));
    }, [bot.publicId, dispatch]);

    const save = async () => {
        try {
            await dispatch(updateTranscriptRoutingSettings(bot.publicId)).unwrap();
            dispatch(showSuccessToastNotification({message: "Transcript routing updated successfully"}));
        } catch {
            dispatch(showErrorToastNotification({message: "Failed to update transcript routing settings"}));
        }
    };

    if (requestStatus === RequestStatus.PENDING) {
        return <div>Loading transcript routing…</div>;
    }

    return <ValidatedForm<TranscriptRoutingSettingsUI> submitForm={save}>
        {(formContext: FormContext<TranscriptRoutingSettingsUI>) => {
            const {formDisabled, onSubmitClick} = formContext;

            return <div className={styles.wrapper}>
                <ValidateWhileEditing editing={hasUnsavedChanges} formContext={formContext}/>
                <Section title="Transcript Routing">
                    <CustomSwitchFieldWithTitle
                        title="Transcript Routing"
                        description="Route completed transcripts using department rules instead of the standard transcript recipients."
                        textOnTrue="On"
                        textOnFalse="Off"
                        valueSelector={transcriptRoutingEnabledSelector}
                        toggleValue={enabled => dispatch(setTranscriptRoutingEnabled(enabled))}
                    />
                </Section>

                <SecondaryCollapsibleSection title="Departments">
                    <TranscriptRoutingDepartments formContext={formContext}/>
                </SecondaryCollapsibleSection>

                <SecondaryCollapsibleSection title="Location Routing">
                    <TranscriptRoutingLocations formContext={formContext}/>
                </SecondaryCollapsibleSection>

                <div className={styles.buttonContainer}>
                    <Button title="Discard Changes" state={hasUnsavedChanges ? ButtonState.SECONDARY : ButtonState.DISABLED}
                            handleClick={() => dispatch(resetTranscriptRoutingSettings())}/>
                    <Button title="Save Changes" state={formDisabled || !hasUnsavedChanges || updateStatus === RequestStatus.PENDING
                        ? ButtonState.DISABLED : ButtonState.PRIMARY} handleClick={onSubmitClick}/>
                </div>
            </div>;
        }}
    </ValidatedForm>;
};
